import {
  LayoutDashboard,
  Briefcase,
  Users,
  Headphones,
  MessagesSquare,
  UserCog,
  Building2,
  Settings,
  Wallet,
  // Handshake, // settlements hidden
  Layers,
  Shield,
  Activity,
  BarChart3,
  CalendarClock,
  AlertTriangle,
} from "lucide-react";
import { OPS_PERMISSIONS as P } from "./permissions";
import { REPORT_NAV_CHILDREN } from "./opsReports";

/**
 * Nav items omit `permissions` when every signed-in ops user can see them.
 * Sidebar hides items unless the user has any listed permission.
 */
export const NAV_GROUPS = [
  {
    label: "Operations",
    items: [
      {
        label: "Ops Overview",
        description: "Queue health, recovery and team performance",
        icon: LayoutDashboard,
        path: "/dashboard",
        end: true,
        permissions: [P.DASHBOARD_VIEW],
      },
      {
        label: "Institutions",
        description: "Service provider clients SimCollect serves",
        icon: Building2,
        path: "/dashboard/institutions",
        permissions: [P.INSTITUTIONS_VIEW],
      },
      {
        label: "Collection Cases",
        description: "Assigned and unassigned recovery cases",
        icon: Briefcase,
        path: "/dashboard/cases/unassigned",
        matchPath: "/dashboard/cases",
        permissions: [P.CASES_VIEW, P.CASES_VIEW_ASSIGNED],
        children: [
          {
            label: "Unassigned",
            description: "Cases that still need an agent",
            path: "/dashboard/cases/unassigned",
            permissions: [P.CASES_ASSIGN, P.ASSIGNMENT_MANAGE],
          },
          {
            label: "Assigned",
            description: "Cases already routed to an agent",
            path: "/dashboard/cases/assigned",
            permissions: [P.CASES_VIEW, P.CASES_VIEW_ASSIGNED],
          },
          {
            label: "Recovery targets",
            description: "Assignment batches scored against the recovery target",
            path: "/dashboard/recovery-targets",
            permissions: [P.CASES_VIEW, P.CASES_VIEW_ASSIGNED],
          },
        ],
      },
    ],
  },
  {
    label: "Debtors & Contact",
    items: [
      {
        label: "Customers",
        description: "Debtor profiles and quick actions",
        icon: Users,
        path: "/dashboard/customers",
        permissions: [P.CUSTOMERS_VIEW],
      },
      {
        label: "Payments",
        description: "Collected payments across institutions",
        icon: Wallet,
        path: "/dashboard/payments",
        permissions: [P.PAYMENTS_VIEW],
      },
      {
        label: "Engagements",
        description: "Calls, promises to pay, and payment plans",
        icon: MessagesSquare,
        path: "/dashboard/engagements",
        permissions: [P.CONTACT_CENTRE_VIEW],
      },
      {
        label: "Follow-ups",
        description: "Scheduled follow-up tasks",
        icon: CalendarClock,
        path: "/dashboard/follow-ups",
        permissions: [P.CONTACT_CENTRE_VIEW],
        badgeKey: "follow_ups",
      },
    ],
  },
  {
    label: "Recovery & Admin",
    items: [
      {
        label: "Escalations",
        description: "Cases sent from an agent to a manager, commercial executive, or legal officer",
        icon: AlertTriangle,
        path: "/dashboard/escalations",
        permissions: [P.ESCALATIONS_VIEW],
      },
      {
        label: "Reports",
        description: "Role-based operational, recovery, and administration reports",
        icon: BarChart3,
        path: "/dashboard/reports",
        matchPath: "/dashboard/reports",
        permissions: [P.REPORTS_VIEW, P.DASHBOARD_VIEW, P.CASES_VIEW, P.CASES_VIEW_ASSIGNED],
        children: REPORT_NAV_CHILDREN,
      },
      // Settlements hidden for now — re-enable when settlement workflow ships.
      // {
      //   label: "Settlements",
      //   description: "Settlement and hardship requests",
      //   icon: Handshake,
      //   path: "/dashboard/settlements",
      //   permissions: [P.SETTLEMENTS_VIEW],
      // },
      {
        label: "Team",
        description: "Onboard and manage ops team members",
        icon: UserCog,
        path: "/dashboard/team",
        permissions: [P.OPS_USERS_VIEW],
      },
      {
        label: "Roles & permissions",
        description: "Configure role access across the ops portal",
        icon: Shield,
        path: "/dashboard/roles",
        permissions: [P.OPS_USERS_MANAGE],
      },
      {
        label: "Settings",
        description: "Profile and preferences",
        icon: Settings,
        path: "/dashboard/settings",
        permissions: [P.SETTINGS_VIEW],
      },
      {
        label: "Audit Trail",
        description: "Immutable log of every portal and system action",
        icon: Activity,
        path: "/dashboard/audit-trail",
        permissions: [P.AUDIT_VIEW],
      },
      {
        label: "SMS log",
        description: "Reminders, uploads, payments, and other texts",
        icon: MessagesSquare,
        path: "/dashboard/sms-logs",
        permissions: [P.CASES_VIEW, P.CASES_VIEW_ASSIGNED, P.AUDIT_VIEW],
      },
    ],
  },
];

export const ALL_NAV_ITEMS = NAV_GROUPS.flatMap((group) => group.items);

export function getPageMeta(pathname) {
  const exact = ALL_NAV_ITEMS.find((item) => item.path === pathname);
  if (exact) return exact;

  if (pathname.match(/^\/dashboard\/institutions\/[^/]+\/portfolio-groups\/[^/]+\/uploads\/[^/]+/)) {
    return {
      label: "File data",
      description: "Accounts in a portfolio upload — assign call centre agents",
      icon: Layers,
      path: pathname,
    };
  }

  if (pathname.match(/^\/dashboard\/institutions\/[^/]+\/portfolio-groups\/[^/]+$/)) {
    return {
      label: "Portfolio group",
      description: "Uploads and cases under this provider group",
      icon: Layers,
      path: pathname,
    };
  }

  if (pathname.match(/^\/dashboard\/institutions\/[^/]+\/portfolio-groups$/)) {
    return {
      label: "Portfolio groups",
      description: "Provider groups and uploaded portfolio files",
      icon: Layers,
      path: pathname,
    };
  }

  if (pathname.match(/^\/dashboard\/institutions\/[^/]+\/customers$/)) {
    return {
      label: "Institution customers",
      description: "Debtors for this institution",
      icon: Users,
      path: pathname,
    };
  }

  if (pathname.startsWith("/dashboard/cases/unassigned")) {
    return {
      label: "Unassigned cases",
      description: "Cases that still need an agent",
      icon: Briefcase,
      path: pathname,
    };
  }

  if (pathname.startsWith("/dashboard/cases/assigned") || pathname.startsWith("/dashboard/cases")) {
    return {
      label: "Assigned cases",
      description: "Cases already routed to an agent",
      icon: Briefcase,
      path: pathname,
    };
  }

  if (pathname.startsWith("/dashboard/reports/")) {
    return {
      label: "Reports",
      description: "Role-based operational, recovery, and administration reports",
      icon: BarChart3,
      path: pathname,
    };
  }

  if (pathname.startsWith("/dashboard/audit-trail") || pathname.startsWith("/dashboard/activity")) {
    return {
      label: "Audit Trail",
      description: "Immutable log of every portal and system action",
      icon: Activity,
      path: "/dashboard/audit-trail",
    };
  }

  if (pathname.startsWith("/dashboard/engagements") || pathname.startsWith("/dashboard/contact-centre")) {
    return {
      label: "Engagements",
      description: "Calls, promises to pay, and payment plans",
      icon: MessagesSquare,
      path: "/dashboard/engagements",
    };
  }

  if (pathname.startsWith("/dashboard/log-contact")) {
    return {
      label: "Log engagement",
      description: "Record call, SMS, WhatsApp or email outreach",
      icon: Headphones,
      path: pathname,
    };
  }

  if (
    pathname.startsWith("/dashboard/escalate-case") ||
    pathname.startsWith("/dashboard/escalations") ||
    pathname.startsWith("/dashboard/legal")
  ) {
    return ALL_NAV_ITEMS[0];
  }

  if (pathname.startsWith("/dashboard/institutions/onboard")) {
    return {
      label: "Onboard Institution",
      description: "Register a client and provision business portal access",
      icon: Building2,
      path: pathname,
    };
  }

  if (pathname.match(/^\/dashboard\/institutions\/[^/]+$/) && !pathname.endsWith("/onboard")) {
    return {
      label: "Institution Details",
      description: "Client profile and recovery overview",
      icon: Building2,
      path: pathname,
    };
  }

  if (pathname.startsWith("/dashboard")) {
    return ALL_NAV_ITEMS[0];
  }

  return {
    label: "Ops Overview",
    description: "Queue health, recovery and team performance",
    icon: LayoutDashboard,
    path: "/dashboard",
  };
}
